import { Link } from 'react-router-dom';
import { AlertTriangle, ExternalLink, ShieldAlert } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog.jsx';

// Dispatch preflight gate (DISPATCH-PREFLIGHT-SPEC).
//
// Two states, and the difference is the whole point:
//   RED  — blockers. The carrier would 4xx/5xx or rate a semantically wrong
//          insured. No fire action at all; the broker leaves via a fix link and
//          rechecks. Nothing is sent.
//   AMBER — warnings only. It will quote, just not the way the deal reads
//          (clamped date, re-rated applicant type, unresolved model). One
//          acknowledgement click and it goes.
//
// Green never reaches this dialog — it fires straight through.

function Row({ item, red, onFix }) {
  const Icon = red ? ShieldAlert : AlertTriangle;
  return (
    <li className="flex gap-2">
      <Icon
        size={14}
        strokeWidth={1.8}
        className={`mt-0.5 shrink-0 ${red ? 'text-accent' : 'text-warning'}`}
        aria-hidden="true"
      />
      <span className="text-[13px] leading-snug text-text">
        {item.message}
        {/* The Global gate carries the whole missing list from the one address
            predicate beside its quote-level message (city and state block the
            quote; the rest block the bind). Rendered, not derived. */}
        {Array.isArray(item.missing) && item.missing.length ? (
          <span className="ml-1.5 text-[12px] text-text-dim" data-missing>missing {item.missing.join(', ')}</span>
        ) : null}
        {/* "Fix" used to be a <Link to={remedy_link}>, and remedy_link is the
            deal route. Fired from inside the deal (which is where the gate
            usually fires) React Router navigated to the URL already on screen
            and nothing happened. It is an ACTION now: the caller closes the
            dialog and opens the surface that actually fixes this blocker. The
            Link stays for callers that read readiness from outside a deal. */}
        {red && onFix ? (
          <button
            type="button"
            onClick={() => onFix(item)}
            className="ml-1.5 inline-flex items-center gap-0.5 whitespace-nowrap text-[12px] font-medium text-info hover:underline"
          >
            Fix<ExternalLink size={11} strokeWidth={2} aria-hidden="true" />
          </button>
        ) : red && item.remedy_link ? (
          <Link
            to={item.remedy_link}
            className="ml-1.5 inline-flex items-center gap-0.5 whitespace-nowrap text-[12px] font-medium text-info hover:underline"
          >
            Fix<ExternalLink size={11} strokeWidth={2} aria-hidden="true" />
          </Link>
        ) : null}
        {/* CLIENT-RECORD-SPEC §5.1: a blocker that knows its fact links to the
            client record with the Incomplete filter on, where that fact is a
            field in place. Same classification, two views. */}
        {red && item.record_link ? (
          <Link to={item.record_link} className="ml-1.5 whitespace-nowrap text-[12px] text-text-muted underline-offset-2 hover:text-text hover:underline" data-record-link>
            on the record
          </Link>
        ) : null}
      </span>
    </li>
  );
}

export default function ReadinessDialog({ open, onOpenChange, carrierName, readiness, busy, onRecheck, onProceed, onFix }) {
  const blockers = readiness?.blockers || [];
  const warnings = readiness?.warnings || [];
  const red = blockers.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {red
              ? `${carrierName}: not ready to quote`
              : `${carrierName}: ${warnings.length} thing${warnings.length === 1 ? '' : 's'} to know`}
          </DialogTitle>
          <DialogDescription>
            {red
              ? 'Nothing has been sent. Fix these on the record, then recheck.'
              : 'This will quote. These change how it rates or routes.'}
          </DialogDescription>
        </DialogHeader>

        {red ? (
          <ul className="space-y-2">
            {blockers.map((b, i) => <Row key={`${b.code}-${i}`} item={b} red onFix={onFix} />)}
          </ul>
        ) : null}

        {warnings.length ? (
          <div className={red ? 'border-t border-border pt-3' : ''}>
            {red ? (
              <p className="mb-2 text-[11px] font-medium uppercase tracking-wide text-text-dim">
                Also worth knowing
              </p>
            ) : null}
            <ul className="space-y-2">
              {warnings.map((w, i) => <Row key={`${w.code}-${i}`} item={w} />)}
            </ul>
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {red ? 'Close' : 'Cancel'}
          </Button>
          {red ? (
            <Button onClick={onRecheck} disabled={busy}>
              {busy ? 'Rechecking…' : 'Recheck'}
            </Button>
          ) : (
            <Button onClick={onProceed} disabled={busy}>
              {busy ? 'Quoting…' : 'Quote anyway'}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
